import { Col, Modal, Row } from 'antd';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import Typo from 'consts/typography';
import { selectCurrentConverter } from 'features/home/homeSlice';
import useThemes from 'hooks/useThemes';
import { BetResponseModel } from 'models';
import { useMemo, useRef, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import { getFuturePrice, getMarketName, getMarketValue } from 'utils/betUtils';
import { isPhone, isTablet, uniqueId } from 'utils/commonUtils';
import { toPng } from 'html-to-image';
import BigNumber from 'bignumber.js';
import { selectTbetPrice } from 'redux/userSlice';
import React from 'react';
import chatApi from 'api/chatApi';

interface ShareComboBetTwitterProps {
  title?: string;
  visible: boolean;
  closeModal: () => void;
  className?: string;
  bet: BetResponseModel;
  tab: number;
}

const ShareComboBetTwitter: React.FunctionComponent<ShareComboBetTwitterProps> = (props) => {
  const { title = '', visible, closeModal, className = '', bet, tab } = props;
  const t = useAppTranslation();
  const platform = useAppSelector(platformStates);
  const { isDesktop, isLargeDesktop } = platform;
  const { currentTheme } = useThemes();
  const SHARE_URL = 'https://terabet.io/';
  const htmlRef = useRef(null);
  const selectedTokenPrice = useAppSelector(selectTbetPrice);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const shareContent = useMemo(() => {
    const status = bet.status === 'WIN' ? 'WON' : bet.status === 'LOSE' ? 'LOST' : 'DREW';
    const amount = bet.status === 'WIN' ? bet.winAmount : bet.amount;
    return `I ${status} ${amount} USD by betting on the combo bet at ${SHARE_URL}`;
  }, [bet.id]);

  const shareWaitingContent = useMemo(() => {
    return `I bet on combo at ${SHARE_URL}`;
  }, [bet.id]);

  const colStyle1 = `col-span-3 primary-text-color m-0 ${
    isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p3
  } font-medium`;
  const colStyle2 = `primary-text-color col-span-2 m-0 ${
    isPhone(platform) ? Typo.p7 : isTablet(platform) ? Typo.p5 : Typo.p3
  } font-normal`;

  const converter = useAppSelector(selectCurrentConverter);
  const details = bet.betDetails;

  const decimalPrice = useMemo(() => {
    return bet.betDetails.reduce(
      (result, curr) =>
        result.times(
          curr.market
            ? new BigNumber(curr.market.decimalPrice)
            : new BigNumber(curr.futureMarket.decimalPrice),
        ),
      new BigNumber(1),
    );
  }, [bet]);

  const winBetAmount = useMemo(() => {
    const winAmount =
      tab === 1
        ? decimalPrice.times(new BigNumber(bet.amount)).minus(new BigNumber(bet.amount))
        : new BigNumber(bet.winAmount);

    return `${winAmount} $`;
  }, [bet.id]);

  const handleDownloadImage = async () => {
    if (htmlRef.current === null) {
      return;
    }
    setIsLoading(true);
    try {
      const dataUrl = await toPng(htmlRef.current);
      const { ok, body } = await chatApi.shareTwitter({
        base64Url: dataUrl.replace('data:image/png;base64,', ''),
        message: tab === 2 ? shareContent : shareWaitingContent,
      });
      if (ok && body) {
        window.open(
          // `https://twitter.com/intent/tweet?text=${tab === 2 ? shareContent : shareWaitingContent}&url=${SHARE_URL}&in_reply_to=${body.tweetId}`,
          `https://twitter.com/intent/retweet?tweet_id=${body.tweetId}`,
          '_blank',
        );
        closeModal();
      }
    } catch (error) {
      console.error('Oops, something went wrong!', error);
    }
    setIsLoading(false);
  };

  return (
    <Modal
      className={className}
      title={title}
      visible={visible}
      onCancel={closeModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      <div ref={htmlRef} className="p-2">
        <div className="share-bet__container share-bet__combo">
          <Row className="my-bet__title">
            <Col flex={4}>
              <p
                className={`text-white ${
                  isDesktop || isLargeDesktop ? Typo.p4 : Typo.p6
                } m-0 text-center`}
              >
                {t('myBet.comboBet')}
              </p>
            </Col>
          </Row>
          {bet.status !== 'WAITING' && (
            <Row className="mt-5 px-2">
              <span
                className={` ${
                  bet.status === 'WIN' ? 'bet-win' : bet.status === 'LOSE' ? 'bet-lose' : 'bet-draw'
                }`}
              >
                {bet.status}
              </span>
            </Row>
          )}
          {details.map((detail) => (
            <React.Fragment key={uniqueId()}>
              {detail.type === 'BET_FUTURE' ? (
                <>
                  <Row className="mt-5 px-2">
                    <span
                      className={`text-white ${
                        isDesktop || isLargeDesktop ? Typo.p5 : Typo.p7
                      } m-0`}
                    >
                      {detail.future.league}
                    </span>
                  </Row>
                  <Row className="mt-5 px-2">
                    <div className="my-bet__content">
                      <p className={colStyle1}>{detail.futureOdds?.name}</p>
                      <p className={colStyle2}>{t('myBet.win')}</p>
                      <p className={colStyle1}>Price</p>
                      <p className={colStyle2}>{getFuturePrice(detail.futureOdds, converter)}</p>
                    </div>
                    <div className="bet-line"></div>
                  </Row>
                </>
              ) : (
                <>
                  <Row className="mt-5 px-2">
                    <span
                      className={`text-white ${
                        isDesktop || isLargeDesktop ? Typo.p5 : Typo.p7
                      } m-0`}
                    >
                      {detail.game.homeTeam} vs {detail.game.awayTeam}
                    </span>
                  </Row>
                  <Row className="mt-5 px-2">
                    <div className="my-bet__content">
                      <p className={colStyle1}>{detail.market.selection}</p>
                      <p className={colStyle2}>{t('myBet.win')}</p>
                      <p className={colStyle1}>{getMarketName(detail.market.market)}</p>
                      <p className={colStyle2}>{getMarketValue(detail.market, converter)}</p>
                    </div>
                    <div className="bet-line"></div>
                  </Row>
                </>
              )}
            </React.Fragment>
          ))}
          <Row className="mt-5 px-2 mb-5">
            <div className="my-bet__content">
              <p className={colStyle1}>{t('myBet.possibleWin')}</p>
              <p className={colStyle2}>{`${winBetAmount} $`}</p>
              <p className={colStyle1}>{t('myBet.betAmount')}</p>
              <p className={colStyle2}>{`${bet.amount} $`}</p>
            </div>
          </Row>
        </div>
      </div>

      <div className="flex-row items-center justify-center">
        <button
          onClick={handleDownloadImage}
          type="button"
          disabled={isLoading}
          className="btn-submit"
        >{`${t('myBetSingle.share')} `}</button>
      </div>
    </Modal>
  );
};

export default ShareComboBetTwitter;
